import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  BrandId,
  BulkResendResultDto,
  CrmStatus,
  LeadDetailDto,
  LeadListItemDto,
  NotificationChannel,
} from '@shared/types';

export interface LeadFilters {
  brand?: BrandId | '';
  form?: string;
  status?: CrmStatus | '';
  q?: string;
  page?: number;
}

export interface LeadListResponse {
  items: LeadListItemDto[];
  total: number;
  page: number;
  pageSize: number;
}

/** Klíč seznamu — filtry jsou jeho součástí, aby se stránkování i hledání cachovalo zvlášť. */
export const leadsKey = (filters: LeadFilters = {}) => ['leads', filters] as const;
export const leadKey = (id: string) => ['lead', id] as const;

function buildQuery(filters: LeadFilters): string {
  const params = new URLSearchParams();
  if (filters.brand) params.set('brand', filters.brand);
  if (filters.form) params.set('form', filters.form);
  if (filters.status) params.set('status', filters.status);
  if (filters.q) params.set('q', filters.q);
  if (filters.page && filters.page > 1) params.set('page', String(filters.page));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useLeads(filters: LeadFilters = {}) {
  return useQuery({
    queryKey: leadsKey(filters),
    queryFn: () => api.get<LeadListResponse>(`/leads${buildQuery(filters)}`),
  });
}

export function useLead(id: string | null) {
  return useQuery({
    queryKey: leadKey(id ?? ''),
    queryFn: () => api.get<LeadDetailDto>(`/leads/${id}`),
    enabled: !!id,
  });
}

/**
 * Společná invalidace po akci nad leadem: detail (nové pokusy/notifikace)
 * i seznam (badge stavu v tabulce).
 */
function useLeadAction<TArgs>(fn: (id: string, args: TArgs) => Promise<LeadDetailDto>, id: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: TArgs) => {
      if (!id) throw new Error('Chybí ID leadu');
      return fn(id, args);
    },
    onSuccess: (lead) => {
      qc.setQueryData(leadKey(lead.id), lead);
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: leadKey(lead.id) });
    },
  });
}

export function useResendCrm(id: string | null) {
  return useLeadAction<void>((leadId) => api.post<LeadDetailDto>(`/leads/${leadId}/resend-crm`), id);
}

export function useResendEvent(id: string | null) {
  return useLeadAction<void>((leadId) => api.post<LeadDetailDto>(`/leads/${leadId}/resend-event`), id);
}

export function useSendEmail(id: string | null) {
  return useLeadAction<void>((leadId) => api.post<LeadDetailDto>(`/leads/${leadId}/send-email`), id);
}

export function useNotify(id: string | null) {
  return useLeadAction<{ channel: NotificationChannel }>(
    (leadId, body) => api.post<LeadDetailDto>(`/leads/${leadId}/notify`, body),
    id,
  );
}

export interface ForwardLeadsBody {
  ids: string[];
  to: string[];
  note?: string;
}

/** Hromadné přeposlání vybraných leadů e-mailem; po odeslání se obnoví seznam i detaily (historie notifikací). */
export function useForwardLeads() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ForwardLeadsBody) =>
      api.post<{ sent: boolean; leads: number }>('/leads/bulk/forward-email', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['lead'] });
    },
  });
}

export interface ComposeEmailBody {
  ids: string[];
  brandId: BrandId | null;
  from: string;
  replyTo: string | null;
  subject: string;
  text: string;
}

export interface ComposeEmailResult {
  sent: number;
  skipped: number;
  failed: number;
  detail: string;
}

/** Vlastní e-mail každému vybranému leadovi; po odeslání se obnoví seznam i detaily. */
export function useComposeEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ComposeEmailBody) => api.post<ComposeEmailResult>('/leads/bulk/send-email', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['lead'] });
    },
  });
}

/** Velikost dávky hromadného odeslání — musí sedět s RESEND_CHUNK ve worker/routes/leads.ts. */
export const BULK_RESEND_CHUNK = 10;

/**
 * Hromadné odeslání do CRM / Events. Výběr se posílá po dávkách sekvenčně, aby se každý
 * request vešel do limitů Workeru; onProgress hlásí počet zpracovaných leadů.
 */
export function useBulkResend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: {
      target: 'crm' | 'events';
      ids: string[];
      includeDelivered: boolean;
      onProgress: (done: number) => void;
    }) => {
      const results: BulkResendResultDto['results'] = [];
      for (let i = 0; i < a.ids.length; i += BULK_RESEND_CHUNK) {
        const ids = a.ids.slice(i, i + BULK_RESEND_CHUNK);
        const r = await api.post<BulkResendResultDto>('/leads/bulk/resend', {
          target: a.target,
          ids,
          includeDelivered: a.includeDelivered,
        });
        results.push(...r.results);
        a.onProgress(Math.min(i + ids.length, a.ids.length));
      }
      return results;
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['lead'] });
    },
  });
}
